import { Cos, Distance, Sin, ToRadians } from "@pdfking/math"
import { QUARTER_TURN, ZERO_DEGREE, ZERO_POINT } from "@pdfking/math/constants"

import { NO_SCALE } from "#constants"
import type { Matrix } from "#public"
import type { GroupTransform } from "#public"

// Read a quarter turn off the table rather than through `Cos`/`Sin`: those give
// 6.1e-17 where the answer is 0, and that dust rides the matrix out through the
// wasm boundary and into goldens. Quarter turns are most of what we plot.
const QUARTERS: [number, number][] = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
]

// A zero times a negative is `-0`, and the matrix is stored and compared, not
// only applied: every zero comes out as `0`.
const unsigned = (value: number): number => (value === 0 ? 0 : value)

/** A group's transform as one matrix:
 *
 * ```
 * p' = translate + origin + R(rotation) · S(scale) · (p − origin)
 * ```
 *
 * Scale applies first, then the rotation, both about `origin`, then the
 * translation. Angles are counter-clockwise, matching the page's y-up space.
 *
 * Every field defaults to identity, so `GroupMatrix({ rotation })` is a plain
 * turn about the origin and `GroupMatrix()` is the identity. */
export const GroupMatrix = ({
  origin = ZERO_POINT,
  rotation = ZERO_DEGREE,
  scale = NO_SCALE,
  translate = ZERO_POINT,
}: GroupTransform = {}): Matrix<Distance> => {
  const turn = ((rotation % 360) + 360) % 360
  const quarter = turn % QUARTER_TURN === 0 ? QUARTERS[turn / QUARTER_TURN] : undefined
  const cos = quarter ? quarter[0] : Cos(ToRadians(rotation))
  const sin = quarter ? quarter[1] : Sin(ToRadians(rotation))

  const a = unsigned(scale.x * cos)
  const b = unsigned(scale.x * sin)
  const c = unsigned(-scale.y * sin)
  const d = unsigned(scale.y * cos)

  return [
    a,
    b,
    c,
    d,
    (origin.x + translate.x - (origin.x * a + origin.y * c)) as Distance,
    (origin.y + translate.y - (origin.x * b + origin.y * d)) as Distance,
  ]
}
